import React, { useState, useEffect, useRef } from "react";
import {
  fetchDurationTypes,
  createDurationType,
  updateDurationType,
  deleteDurationType,
} from "../../api/adminMasterApi";
import { getCsrfToken } from "../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalTextField,
  GlobalSelectField,
} from "../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../global/GlobalModal";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../global/GlobalBadge";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const DurationTypeMaster = ({ onDirectoryChange, canCreate = false, canEdit = false, canDelete = false, }) => {
  const [durations, setDurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    duration_name: "",
    duration_in_days: "",
    description: "",
    status_id: 1,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);
  const [successMsg, setSuccessMsg] = useState("");

  const scrollToMasterForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  useEffect(() => {
    loadDurations();
  }, []);

  const loadDurations = async () => {
    try {
      setLoading(true);
      const response = await fetchDurationTypes();
      if (response && response.success) {
        setDurations(response.data || []);
      } else {
        setDurations(response || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load duration types");
    } finally {
      setLoading(false);
    }
  };

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrorDetails[name]) {
      setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isEditing ? !canEdit : !canCreate) return;
    setFormError(null);
    setFormErrorDetails({});
    setSuccessMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      const days = Number(formData.duration_in_days);
      if (!Number.isFinite(days) || days <= 0) {
        setFormError("Duration in days must be a number greater than 0.");
        return;
      }

      let result;
      if (isEditing) {
        result = await updateDurationType(
          formData.id,
          {
            duration_name: formData.duration_name.trim(),
            duration_in_days: days,
            description: formData.description,
            status_id: Number(formData.status_id),
          },
          csrfToken,
        );
      } else {
        result = await createDurationType(
          {
            duration_name: formData.duration_name,
            duration_in_days: days,
            description: formData.description,
          },
          csrfToken,
        );
      }
      if (result?.error) {
        setFormError(result.error.message || "Failed to save duration type");
        setFormErrorDetails(result.error.details || {});
        setTimeout(() => alertRef.current?.focus(), 0);
        return;
      }
      if (result?.reactivated) {
        setSuccessMsg("Duration type restored to the directory.");
      } else {
        setSuccessMsg(
          isEditing
            ? "Duration type updated successfully."
            : "Duration type created successfully.",
        );
      }
      handleCancel();
      await loadDurations();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message);
      setTimeout(() => alertRef.current?.focus(), 0);
    }
  };

  const handleEdit = (duration) => {
    setSuccessMsg("");
    setFormData({
      id: duration.id,
      duration_name: duration.duration_name,
      duration_in_days: duration.duration_in_days ?? "",
      description: duration.description || "",
      status_id:
        duration.status_id != null && duration.status_id !== ""
          ? Number(duration.status_id)
          : 1,
    });
    setIsEditing(true);
    scrollToMasterForm();
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteDurationType(itemToDelete.id, csrfToken);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setSuccessMsg("Duration type removed from the directory.");
      await loadDurations();
      onDirectoryChange?.();
    } catch (err) {
      alert("Failed to remove: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      id: null,
      duration_name: "",
      duration_in_days: "",
      description: "",
      status_id: 1,
    });
    setFormError(null);
    setFormErrorDetails({});
    setIsEditing(false);
  };

  if (loading) return <p className="ub-loading">Loading Duration Types…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  const dataList = Array.isArray(durations) ? durations : durations.data || [];
  const ALERT_ID = "ms-duration-master-alert";

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
        }}
      >
        <h3 className="ub-section__subhead" style={{ margin: 0 }}>
          Duration Type Master
        </h3>
        {/* <GlobalButton
          variant="primary"
          size="sm"
          onClick={() => {
            setSuccessMsg("");
            setFormData({
              id: null,
              duration_name: "",
              duration_in_days: "",
              description: "",
              status_id: 1,
            });
            setIsEditing(false);
            setFormError(null);
            setFormErrorDetails({});
            scrollToMasterForm();
          }}
        >
          Add New Duration
        </GlobalButton> */}
      </div>

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        <GlobalFormShell
          legend={isEditing ? "Edit duration type" : "Add duration type"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton variant="ghost" onClick={handleCancel}>
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save duration" : "Create duration"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id={isEditing ? "duration_name_edit" : "duration_name"}
            label="Duration Name"
            value={formData.duration_name}
            onChange={(v) => setFormField("duration_name", v)}
            error={formErrorDetails.duration_name}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id={isEditing ? "duration_in_days_edit" : "duration_in_days"}
            label="Duration in Days"
            type="number"
            min={0}
            value={formData.duration_in_days}
            onChange={(v) => setFormField("duration_in_days", v)}
            error={formErrorDetails.duration_in_days}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id={isEditing ? "description_edit" : "description"}
            label="Description"
            value={formData.description}
            onChange={(v) => setFormField("description", v)}
            error={formErrorDetails.description}
            baseAlertId={ALERT_ID}
          />
          {isEditing ? (
            <GlobalSelectField
              id="duration_status_id"
              label="Status"
              value={String(formData.status_id)}
              onChange={(v) => setFormField("status_id", Number(v))}
              options={STATUS_OPTIONS}
              error={formErrorDetails.status_id}
              baseAlertId={ALERT_ID}
            />
          ) : null}
        </GlobalFormShell>
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Duration type master table"
        headers={[
          { key: "name", label: "Duration Name" },
          { key: "days", label: "Duration in Days" },
          { key: "desc", label: "Description" },
          { key: "status", label: "Status" },
          { key: "created", label: "Created Date" },
          {
            key: "actions",
            label: "Actions",
            className: "ub-table__col-actions",
          },
        ]}
      >
        {dataList.map((duration) => (
          <tr key={duration.id}>
            <td style={{ fontWeight: "500" }}>{duration.duration_name}</td>
            <td>{duration.duration_in_days} days</td>
            <td>{duration.description || "—"}</td>
            <td>
              <GlobalBadge active={Number(duration.status_id) === 1} />
            </td>
            <td>
              {duration.created_at
                ? new Date(duration.created_at).toLocaleDateString()
                : "—"}
            </td>
            <td className="ub-table__col-actions">
              <div className="ub-table-actions">{canEdit ? (<GlobalTableActionButton action="edit" onClick={() => handleEdit(duration)} />) : null}{canDelete ? (<GlobalTableActionButton action="delete" onClick={() => handleDeleteClick(duration)} />) : null}</div>
            </td>
          </tr>
        ))}
        {dataList.length === 0 && (
          <tr>
            <td
              colSpan={6}
              style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
            >
              No Duration Types Found
            </td>
          </tr>
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove duration type <b>{itemToDelete?.duration_name}</b> from the
          directory? It will be hidden from lists (marked inactive).
        </p>
      </GlobalModal>
    </div>
  );
};

export default DurationTypeMaster;
